import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

const empty = { opponent: "", date: new Date().toISOString().slice(0,10),
  competition: "", result: "1-0", color: "Brancas", eco: "", pgn: "", notes: "" };

export default function Games() {
  const [games, setGames] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [selected, setSelected] = useState(null);
  // Mantido para compatibilidade com o fluxo histórico de análise; a interface está desativada.
  const [analyzing, setAnalyzing] = useState(false);
  const load = () => api.get("/games").then(r => setGames(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    try {
      const { data: created } = await api.post("/games", form);
      toast.success("Partida registrada"); setForm(empty); setOpen(false); load(); setSelected(created);
    } catch { toast.error("Erro ao salvar"); }
  };
  const remove = async (id) => { await api.delete(`/games/${id}`); toast.success("Removida"); load(); if (selected?.id === id) setSelected(null); };
  const analyze = async (g) => {
    setAnalyzing(true); setSelected({ ...g, analysis: null });
    try {
      await api.post(`/games/${g.id}/analyze`);
      // Poll until done or error (max ~180s)
      for (let i = 0; i < 90; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const { data } = await api.get(`/games/${g.id}/analysis`);
        if (data.status === "done") {
          setSelected({ ...g, analysis: data.analysis }); load();
          toast.success("Análise concluída");
          return;
        }
        if (data.status === "error") {
          toast.error("Falha na análise: " + (data.error || "erro"));
          return;
        }
      }
      toast.error("Tempo esgotado aguardando análise.");
    } catch (err) { toast.error("Falha ao iniciar análise: " + (err.response?.data?.detail || err.message)); }
    finally { setAnalyzing(false); }
  };

  return (
    <AppLayout kind="athlete">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl text-white font-semibold">Minhas Partidas</h1>
          <p className="text-slate-400 mt-1">Registro completo com análise por IA.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-game-btn" className="bg-amber-500 text-[#071A33] hover:bg-amber-400">+ Nova Partida</Button>
          </DialogTrigger>
          <DialogContent className="bg-[#071A33] border-amber-500/30 text-white max-w-2xl">
            <DialogHeader><DialogTitle>Registrar Partida</DialogTitle></DialogHeader>
            <form onSubmit={save} className="grid sm:grid-cols-2 gap-4">
              <Field label="Adversário" value={form.opponent} onChange={(v) => set("opponent", v)} testid="game-opponent" required />
              <Field label="Data" type="date" value={form.date} onChange={(v) => set("date", v)} testid="game-date" required />
              <Field label="Competição" value={form.competition} onChange={(v) => set("competition", v)} />
              <div className="space-y-2"><Label>Resultado</Label>
                <Select value={form.result} onValueChange={(v) => set("result", v)}>
                  <SelectTrigger data-testid="game-result" className="bg-[#0D2A52] border-amber-500/20"><SelectValue /></SelectTrigger>
                  <SelectContent>{["1-0","0-1","½-½"].map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Cor</Label>
                <Select value={form.color} onValueChange={(v) => set("color", v)}>
                  <SelectTrigger className="bg-[#0D2A52] border-amber-500/20"><SelectValue /></SelectTrigger>
                  <SelectContent>{["Brancas","Pretas"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Field label="ECO" value={form.eco} onChange={(v) => set("eco", v)} />
              <div className="sm:col-span-2 space-y-2">
                <Label>PGN</Label>
                <Textarea rows={4} value={form.pgn} onChange={(e) => set("pgn", e.target.value)} data-testid="game-pgn"
                  className="bg-[#0D2A52] border-amber-500/20 font-mono text-xs" placeholder='[Event "..."]&#10;1. e4 e5 ...' />
              </div>
              <div className="sm:col-span-2 space-y-2">
                <Label>Observações</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)}
                  className="bg-[#0D2A52] border-amber-500/20" />
              </div>
              <Button type="submit" data-testid="game-save" className="sm:col-span-2 bg-amber-500 text-[#071A33] hover:bg-amber-400 font-semibold">Salvar</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {!games.length ? (
        <Card className="bg-transparent gsx-card-dark min-h-[calc(100vh-14rem)] w-full flex flex-col items-center justify-center p-8 text-center">
          <div className="text-xl font-semibold text-white">Nenhuma partida registrada.</div>
          <p className="mt-2 max-w-md text-sm text-slate-400">Você ainda não registrou nenhuma partida. Comece adicionando sua primeira partida.</p>
        </Card>
      ) : <Card className="bg-transparent gsx-card-dark p-4 sm:p-6">
        <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-3 lg:border-r lg:border-white/10 lg:pr-6">
          {games.map(g => (
            <Card key={g.id} onClick={() => setSelected(g)}
              className={`bg-transparent gsx-card-dark p-4 cursor-pointer ${selected?.id === g.id ? "ring-1 ring-amber-500" : ""}`}
              data-testid={`game-item-${g.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-white font-medium">vs {g.opponent}</div>
                  <div className="text-xs text-slate-400">{g.date} • {g.color} • {g.result}</div>
                </div>
                <Button size="icon" variant="ghost" onClick={(e) => { e.stopPropagation(); remove(g.id); }}
                  className="text-red-400 hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </Card>
          ))}
        </div>

        <div className="lg:col-span-2 lg:pl-1">
          {selected ? (
            <div className="space-y-5">
              <div className="flex justify-between items-start flex-wrap gap-3 border-b border-white/10 pb-4">
                <div>
                  <p className="text-xs uppercase tracking-widest text-amber-300">Detalhes da partida</p>
                  <h3 className="mt-1 text-xl text-white font-semibold">vs {selected.opponent}</h3>
                  <p className="text-xs text-slate-400 mt-1">{selected.date} • {selected.competition || "Amistoso"} • {selected.color} • {selected.result}</p>
                </div>
              </div>
              <div className="grid gap-3 sm:grid-cols-3">
                <div className="rounded-xl border border-amber-400/25 bg-amber-500/10 p-4"><p className="text-xs uppercase tracking-widest text-amber-300">Placar</p><p className="mt-2 text-3xl font-bold text-white">{selected.result}</p><p className="mt-1 text-xs text-slate-400">{selected.color}</p></div>
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4"><p className="text-xs uppercase tracking-widest text-slate-400">Competição</p><p className="mt-2 text-sm font-medium text-white">{selected.competition || "Amistoso"}</p></div>
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4"><p className="text-xs uppercase tracking-widest text-slate-400">ECO</p><p className="mt-2 text-sm font-medium text-white">{selected.eco || "Não informado"}</p></div>
              </div>
              {selected.notes && <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4"><p className="text-xs uppercase tracking-widest text-amber-300">Observações</p><p className="mt-2 whitespace-pre-wrap text-sm text-slate-300">{selected.notes}</p></div>}
              {selected.pgn && (
                <pre className="mt-4 text-xs bg-black/30 p-3 rounded-lg font-mono text-slate-300 overflow-x-auto max-h-56">{selected.pgn}</pre>
              )}
              {selected.analysis && (
                <div className="mt-6" data-testid="game-analysis">
                  <div className="text-amber-300 text-xs uppercase tracking-widest mb-2">Análise IA</div>
                  <div className="text-slate-200 text-sm whitespace-pre-wrap leading-relaxed">{selected.analysis}</div>
                </div>
              )}
            </div>
          ) : (
            <Card className="bg-transparent gsx-card-dark p-10 text-center text-slate-400">
              Selecione uma partida para ver detalhes.
            </Card>
          )}
        </div>
        </div>
      </Card>}
    </AppLayout>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Input type={type} value={value} required={required} data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
        className="bg-[#0D2A52] border-amber-500/20 text-white" />
    </div>
  );
}
